import { GlassWater, UserCheck } from 'lucide-react'
import { useSoiree } from './SoireeData.js'
import { NomsAssociations } from './Rattachements.js'
import { ligneVenue, noteBar, noteVenue, partEntree, pctFr, type CompteFormule } from './formules-model.js'

/**
 * LES BILLETS DES ASSOCIATIONS, FORMULE PAR FORMULE (19/10/2026).
 *
 * Deux tuiles de la fiche d'une soirée, nourries par le détail (relu toutes
 * les 30 s : le bar suit en direct), sur toute la nuit — les événements
 * d'associations de la soirée, ou de la soirée de cet événement :
 *
 *   · « Venue des associations » — billets vendus face aux entrées, par
 *     formule et par association. Une fois la nuit finie : la part de billets
 *     payés jamais scannés (le « no-show »), pour négocier la suivante ;
 *   · « Formules pour le bar » — combien d'Essentiel, de Soft, de Prestige,
 *     et ce que chacune comprend : de quoi préparer le bar.
 *
 * Sans billet d'association, ou réglage éteint, la page retire la tuile
 * (`tuilesFormulesAbsentes`) : jamais de tuile vide.
 */

/*
 * CE QUE LA FORMULE COMPREND, SUR SA PROPRE LIGNE (revue du 19/10/2026) :
 * accolé au nom dans `.so-break__name` (une ligne, points de suspension), il
 * devenait « Essentiel · pu… » à 390 px et à 1024 px — or c'est l'information
 * du bar. Le nom et les nombres se replient (`so-break__head--formule`), le
 * contenu passe dessous et revient à la ligne au lieu d'être coupé.
 */
function LigneFormule({ f, fini, mesure }: { f: CompteFormule; fini: boolean; mesure: 'venue' | 'bar' }) {
  const part = partEntree(f)
  return (
    <div className="so-break">
      <div className="so-break__head so-break__head--formule">
        <span className="so-break__name so-break__name--formule">
          {f.libelle}
          {f.contenu ? <span className="so-break__contenu">{f.contenu}</span> : null}
        </span>
        <span className="so-break__nums">
          <strong>{f.entres.toLocaleString('fr-FR')}</strong> / {f.vendus.toLocaleString('fr-FR')} entrés
          {mesure === 'venue' && fini && f.absents_pct !== null ? <em>{pctFr(f.absents_pct)} absents</em> : null}
          {mesure === 'bar' && !fini && f.pas_entres > 0 ? <em>{f.pas_entres.toLocaleString('fr-FR')} attendus</em> : null}
        </span>
      </div>
      <div className="so-break__bar" aria-hidden="true"><span style={{ width: `${Math.round(part * 100)}%` }} /></div>
    </div>
  )
}

export function VenueAssociationsWidget() {
  const { detail } = useSoiree()
  const v = detail?.formules ?? null
  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Venue des associations">
      <header className="lc-panel__head">
        <h2 className="lc-section-title"><UserCheck size={16} aria-hidden="true" /> Venue des associations</h2>
      </header>
      <div className="lc-panel__body lc-panel__body--flush so-breakdown so-breakdown--asso">
        {v ? (
          <>
            <p className="so-formules__phrase">{v.phrase_venue}</p>
            <h3 className="so-formules__titre">Par formule</h3>
            {v.formules.map((f) => <LigneFormule key={`${f.cle}-${f.libelle}`} f={f} fini={v.fini} mesure="venue" />)}
            {v.associations.length > 1 ? (
              <>
                <h3 className="so-formules__titre">Par association</h3>
                <ul className="so-formules__assos">
                  {v.associations.map((a) => (
                    <li key={a.id} className="so-formules__asso">
                      {/* Chaque ligne nommée par son association (20/10/2026) : « INTERSANTE PHARMA · Pharmacie ». */}
                      <span className="so-formules__asso-nom">{a.name}
                        <NomsAssociations associations={detail?.associations?.par_evenement[a.id] ? [detail.associations.par_evenement[a.id]!] : undefined} /></span>
                      <span className="so-formules__asso-n">{ligneVenue(a, v.fini, { lecture_complete: v.lecture_complete })}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            <p className="lc-hint so-pad">{noteVenue(v)}</p>
          </>
        ) : <p className="lc-hint so-pad">{detail ? 'Aucun billet d’association pour cette soirée.' : 'Chargement…'}</p>}
      </div>
    </section>
  )
}

export function FormulesBarWidget() {
  const { detail } = useSoiree()
  const v = detail?.formules ?? null
  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Formules pour le bar">
      <header className="lc-panel__head">
        <h2 className="lc-section-title"><GlassWater size={16} aria-hidden="true" /> Formules pour le bar</h2>
      </header>
      <div className="lc-panel__body lc-panel__body--flush so-breakdown so-breakdown--asso">
        {v ? (
          <>
            <p className="so-formules__phrase">{v.phrase_bar}</p>
            {v.formules.map((f) => <LigneFormule key={`${f.cle}-${f.libelle}`} f={f} fini={v.fini} mesure="bar" />)}
            <p className="lc-hint so-pad">{noteBar(v, Date.now())}</p>
          </>
        ) : <p className="lc-hint so-pad">{detail ? 'Aucun billet d’association pour cette soirée.' : 'Chargement…'}</p>}
      </div>
    </section>
  )
}
